import React, { useState } from "react";
import EditIcon from "../../../assets/icons/edit";
import SavedIcon from "../../../assets/icons/saved";
import { ReactComponent as SavingIcon } from "../../../assets/icons/saving.svg";
import useObjectInput from "../../../hooks/useObjectInput";
import GOIButton from "../GOIHeaderSection/GOIButton";
import GOITabs from "../GOIHeaderSection/GOITabs";
import GOIRenderTabContent from "../GOIRenderTabContent";
import NodeHeader from "./NodeHeader";
import NodeHeaderWithGOINode from "./NodeHeaderWithGOINode";

// renders the child nodes, if the node has children, then it will render the GOITree basically doing the recursion
// if the node has no children, then it will only render the Node
const RenderChildNodes = ({
  item,
  level,
  showFields,
  toggleShowFields,
  saving,
  margin,
  editingChildrenIds,
}) => {
  // function to check if the node has children
  const hasChildren = (child) =>
    child.children && Object.values(child.children).length > 0;

  return Object.values(item.children).map((child) => (
    <div key={child.id}>
      {hasChildren(child) ? (
        <GOITree
          level={level + 1}
          item={child}
          key={child.id}
          margin={level > 1 ? margin + 26 : margin}
          savingParentValue={saving}
        />
      ) : (
        // simply show the node
        <div className="mt-10">
          <NodeHeaderWithGOINode
            showField={showFields[child.id]}
            item={child}
            toggleShowFields={toggleShowFields}
            margin={margin}
            level={level}
            saving={saving}
            editMode={editingChildrenIds[child.id]}
          />
        </div>
      )}
    </div>
  ));
};

// renders the tree view
const GOITree = ({ item, level = 0, margin = 26, savingParentValue }) => {
  // clicking on node title will show/hide the children
  const [showChildren, setShowChildren] = useState(true);
  const [state, setState] = useState({
    isSaving: false,
    activeTab: "",
  });

  const marginLeft = `${margin}px`;

  // context hook to manage all the state and functions related to tree nodes
  const {
    saveData,
    createRequestData,
    oneChildrenIsInEditMode,
    showFields,
    toggleShowFields,
    changeVisibleHeaderValues,
    visibleHeaderIds,
    editingChildrenIds,
  } = useObjectInput();

  // toggles the children node visibility
  const toggleChildren = () => {
    if (showChildren && showHeader) {
      // if header is visible, then we need to hide the header with the children
      changeVisibleHeaderValues(item.id, false);
    }
    setShowChildren(!showChildren);
  };

  // toggles the header visibility
  const toggleShowHeader = () => {
    changeVisibleHeaderValues(item.id);
  };

  const onStateChange = (key, value) => {
    setState({
      ...state,
      [key]: value,
    });
  };

  // callback function to be called after the data save request is completed
  const cbFunction = () => {
    onStateChange("isSaving", false);
  };

  // function to handle save button click
  const handleSaveBtnClick = () => {
    onStateChange("isSaving", true);
    const data = createRequestData(item);
    saveData(data, "", cbFunction, cbFunction);
  };

  const renderStatus = () => {
    return isSaving || savingParentValue ? (
      <>
        <SavingIcon />
        <p className="goi-header-section-saving">Saving...</p>
      </>
    ) : !editMode ? (
      <>
        <SavedIcon className="goi-header-section-saved-icon" />
        <p>Saved</p>
      </>
    ) : (
      <>
        <EditIcon />
        <p>Editing</p>
      </>
    );
  };

  // check if any of the children is in edit mode
  const editMode = oneChildrenIsInEditMode(item.childrenIds);
  const inputItem = item;
  const { activeTab, isSaving } = state;
  const showHeader = visibleHeaderIds[item.id];
  const renderChildren = showChildren && item.children;

  return (
    <div className={level === null ? "pb-10" : ""}>
      <div className="nodes-tree">
        {/* no need to show header of currently active object as its already visible at the top */}
        {level !== 0 && level !== null && (
          <div className="mt-10">
            <NodeHeader
              style={{
                cursor: "pointer",
                marginLeft: level > 1 ? marginLeft : "0px",
              }}
              toggleShowHide={toggleChildren}
              item={item}
              editMode={editMode}
              showChildren={showChildren}
              showHeader={showHeader}
              toggleShowHeader={toggleShowHeader}
            />
            {showHeader && (
              <div className={`generated-object-input mx-15 mt-10`}>
                <div className="goi-header-section-outer">
                  <div className="goi-header-section-editing-outer px-10">
                    <div className="goi-header-section-editing-wrapper">
                      {renderStatus()}
                    </div>
                    <div className="goi-header-section-right-wrapper">
                      <GOITabs
                        activeTab={activeTab}
                        setActiveTab={(value) =>
                          setState({ ...state, activeTab: value })
                        }
                        data={inputItem}
                      />
                      <p>{inputItem.class}</p>
                      <GOIButton
                        editMode={editMode}
                        handleSaveBtnClick={handleSaveBtnClick}
                      />
                    </div>
                  </div>
                </div>
                <GOIRenderTabContent activeTab={activeTab} data={inputItem} />
              </div>
            )}
          </div>
        )}
        <div>
          {renderChildren && (
            <RenderChildNodes
              item={item}
              level={level}
              showFields={showFields}
              toggleShowFields={toggleShowFields}
              saving={isSaving || savingParentValue}
              margin={margin}
              editingChildrenIds={editingChildrenIds}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default GOITree;
